import { Button, Divider } from '@design-system';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { AnalyticsFlowProvider } from '@utils/analytics/flow';
import { formatArtistNames, renderShopifyArtistName } from '@utils/artists';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';

import {
  FF_FRAMING_CAROUSEL,
  FF_FRAMING_PRESELECTION,
} from '../../../../constants/featureFlags';
import { PreAuthDrawButton } from '../../../../features/draws/components/PreAuthDrawButton';
import { buildDrawFlowConfig } from '../../../../features/draws/utils/useDrawFlow';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useDrawStage } from '../../../../hooks/useDrawStage';
import { useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { useUrlHasParam } from '../../../../hooks/useUrlHasParam';
import { useFlow } from '../../../../providers';
import { useDraws } from '../../../../providers/DrawProvider';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { isInNextSixDays } from '../../../../utils/product';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import DialogFlowContainer from '../../Dialog/DialogFlowContainer';
import { DialogFlowWithStripe } from '../../Dialog/DialogFlowWithStripe';
import ProductDate from '../../ProductDate';
import { DrawExplainer } from './DrawExplainer';
import { MultiProductDrawFlow } from './MultiProductDrawFlow';
import { PreOrderHeader } from './PreOrderHeader';

export const DrawFlow = () => {
  const { t } = useTranslation(['product', 'authentication']);
  const [showDialog, setShowDialog] = useState(false);
  const { startFlow } = useFlow();

  const { drawIdsEntered, linkedDraws, mainDraw } = useDraws();
  const stage = useDrawStage(mainDraw?.draw);
  const isPreOrder = useIsPreOrder(mainDraw?.draw);

  const [currency] = useCurrency();

  const hasCheckoutParam = useUrlHasParam('checkout_id');
  const {
    activeDraw,
    customerSelectedFrame,
    entryIntentResponse,
    resetController,
    setupSecret,
  } = usePreAuthDrawCtx();

  const isFramedByDefaultFeatureEnabled = useFeatureIsOn('framed-by-default');
  const experimentalFramingCarousel = useFeatureIsOn(FF_FRAMING_CAROUSEL);
  const experimentalFramingPreselection = useFeatureIsOn(
    FF_FRAMING_PRESELECTION,
  );

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'draw',
    });
  }, [emitter]);

  const handleFlowClose = useCallback(() => {
    resetController();
  }, [resetController]);

  const drawButtons = useMemo(() => {
    if (!mainDraw) {
      return null;
    }

    if (drawIdsEntered.length === linkedDraws.length + 1) {
      return (
        <Button
          disabled
          fullWidth
          icon="ic_check"
          label={t('subscriptionCta.draw.buttonSuccessTitle')}
          size="lg"
          variant="positive"
        />
      );
    }

    if (linkedDraws.length === 0) {
      const flowConfig = buildDrawFlowConfig(
        t,
        mainDraw,
        isPreOrder,
        isFramedByDefaultFeatureEnabled,
      );
      return (
        <PreAuthDrawButton
          buttonLocation="mainCta"
          drawWithFrames={mainDraw}
          hasCheckoutParam={hasCheckoutParam}
          startDrawFlow={() => {
            startFlow({
              ...flowConfig,
              onFlowComplete: handleFlowClose,
            });
          }}
        />
      );
    }

    return (
      <MultiProductDrawFlow
        linkedDraws={linkedDraws}
        mainDraw={mainDraw}
        t={t}
      />
    );
  }, [
    mainDraw,
    linkedDraws,
    t,
    isPreOrder,
    isFramedByDefaultFeatureEnabled,
    hasCheckoutParam,
    startFlow,
    handleFlowClose,
    drawIdsEntered,
  ]);

  if (!mainDraw) {
    return null;
  }

  return (
    <>
      {stage === 'DRAW_PRE_ORDER_CLOSED' && (
        <>
          <PreOrderHeader active={false} title={t('preOrder.isClosed.title')} />
          <Divider className="my-6" variant="dashed" />
        </>
      )}
      <div className="mb-6 flex w-full flex-col space-y-3 text-center">
        <div>
          <p className="w-full font-bold">
            {isInNextSixDays(new Date(mainDraw.draw.closeAt))
              ? t('subscriptionDate.draw.countdown')
              : t('subscriptionDate.draw.date')}
          </p>
          <p className="w-full">
            <ProductDate date={new Date(mainDraw.draw.closeAt)} />
          </p>
        </div>
        {mainDraw.draw.allocation && (
          <p className="w-full text-neutral-4">
            {t(
              stage === 'DRAW_PRE_ORDER_CLOSED'
                ? 'subscriptionCta.preOrder.titleAllocation'
                : 'subscriptionCta.draw.titleAllocation',
              { amount: mainDraw.draw.allocation },
            )}
          </p>
        )}
      </div>
      {drawButtons}
      <button
        className="hyperlink mt-3"
        onClick={() => {
          setShowDialog(true);
          handleHowToClick();
        }}
        type="button"
      >
        {t('subscriptionCta.draw.learnMoreDialog.title')}
      </button>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('subscriptionCta.draw.learnMoreDialog.title')}
      >
        <DrawExplainer
          cryptoComEnabled={mainDraw.draw.cryptoComEnabled && !isPreOrder}
        />
        {showDialog && drawButtons}
      </SimpleDialog>
      <DialogFlowContainer containerId="multi-product-draw" />
      <AnalyticsFlowProvider
        name="draw"
        properties={{
          'product title': activeDraw?.product.title,
          'artist name': renderShopifyArtistName(
            activeDraw?.product.artists[0]?.name ?? '',
          ),
          'product price': activeDraw?.product.price,
          'product can be framed': Boolean(activeDraw?.frames?.length),
          'product frame upgrade available': Boolean(activeDraw?.frames),
          'product frame chosen': Boolean(customerSelectedFrame),

          experimentalFramingCarousel,
          experimentalFramingPreselection,
          ecommerce: {
            currency,
            transaction_id: entryIntentResponse.intent?.id,
            value: activeDraw?.product.price ?? '',
            items: [
              {
                item_id: activeDraw?.product.shopifyId,
                item_name: activeDraw?.product.title,
                item_brand: formatArtistNames(
                  activeDraw?.product.artistIds ?? [],
                ),
                item_category: activeDraw?.product.medium,
                price: activeDraw?.product.price,
                quantity: 1,
              },
            ],
          },
        }}
      >
        {() =>
          setupSecret && <DialogFlowWithStripe clientSecret={setupSecret} />
        }
      </AnalyticsFlowProvider>
    </>
  );
};
